import { useState, useEffect, useRef, useMemo } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { Pill, Plus, X, Search, Check, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react'
import { useProfileStore } from '../../stores'
import PharmacyLookup from '../../components/PharmacyLookup'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'
import { useI18n } from '../../i18n'
import { medicationProvenance, medicationTimespan } from '../../utils/medicationProvenance'
import { COMMON_MEDICATIONS, MED_DOSE_UNITS, MED_FREQUENCIES, parseDose, formatDose } from '../../data/medicalLists'
import WheelPicker from '../../components/WheelPicker'
import { resolveProfileReturn } from '../../utils/profileReturnTo'
import { clinicianForms, clinicianFromText, clinicianName } from '../../utils/clinicianName.js'

const MED_TYPES_STRUCTURE = [
  { id: 'chronic', labelKey: 'typeChronic', descKey: 'dailyLongterm' },
  { id: 'course', labelKey: 'typeCourse', descKey: 'antibioticsShortterm' },
  { id: 'supplement', labelKey: 'typeSupplement', descKey: 'vitaminsOtc' },
  { id: 'otc', labelKey: 'typeOTC', descKey: 'ibuprofenAllergyMeds' },
]

const COURSE_DURATIONS = [
  { days: 3, label: '3d' }, { days: 5, label: '5d' }, { days: 7, label: '7d' },
  { days: 10, label: '10d' }, { days: 14, label: '14d' }, { days: 21, label: '21d' },
  { days: 30, label: '30d' }, { days: 60, label: '60d' }, { days: 90, label: '90d' },
]

const DISCONTINUE_REASON_KEYS = [
  'discontinueFinished', 'discontinueSideEffects', 'discontinueDoctorAdvised',
  'discontinueNotEffective', 'discontinueCostInsurance', 'discontinueOther',
]

function inferMedType(name, category) {
  const lc = (category || '').toLowerCase()
  const ln = (name || '').toLowerCase()
  if (lc.includes('vitamin') || lc.includes('supplement') || ln.includes('vitamin') || ln.includes('multivitamin') || ln.includes('probiotic') || ln.includes('fish oil') || ln.includes('melatonin')) return 'supplement'
  if (lc.includes('antibiotic') || lc.includes('antifungal') || lc.includes('antiviral')) return 'course'
  if (ln.includes('ibuprofen') || ln.includes('acetaminophen') || ln.includes('benadryl')) return 'otc'
  return 'chronic'
}

function daysRemaining(med) {
  const ed = med.endDate || med.end_date
  if (!ed) return null
  return Math.ceil((new Date(ed) - new Date()) / (1000 * 60 * 60 * 24))
}

const DEFAULT_MED = {
  name: '', dosage: '', doseAmount: '', doseUnit: 'mg', frequency: 'Once daily', purpose: '', dateStarted: '',
  medType: 'chronic', durationDays: null, endDate: null,
  status: 'active', source: 'self_reported',
}

export default function ProfileMedications() {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, updateProfile } = useProfileStore()
  const { t } = useI18n()

  const MED_TYPES = useMemo(() => MED_TYPES_STRUCTURE.map(m => ({
    ...m, label: t('profileMedications.' + m.labelKey),
  })), [t])

  const FREQ_OPTS = useMemo(() => MED_FREQUENCIES.map(f => ({
    value: f.value, label: t('profileMedications.' + f.key),
  })), [t])

  const DISCONTINUE_REASONS = useMemo(() =>
    DISCONTINUE_REASON_KEYS.map(k => t('profileMedications.' + k)),
  [t])

  const [hasMedications, setHasMedications] = useState(null)
  const [medications, setMedications] = useState([])
  const [preferredPharmacy, setPreferredPharmacy] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingMed, setEditingMed] = useState(null) // the med object being edited (has .id for ledger)
  const [newMedication, setNewMedication] = useState({ ...DEFAULT_MED })
  const [medSearch, setMedSearch] = useState('')
  const [showDiscontinueModal, setShowDiscontinueModal] = useState(null) // med object
  const [discontinueReason, setDiscontinueReason] = useState('')
  const [showPastMeds, setShowPastMeds] = useState(false)
  const [showNoneConfirm, setShowNoneConfirm] = useState(false)
  const [movingToPast, setMovingToPast] = useState(false)
  const [loading, setLoading] = useState(true)
  const [useLedger, setUseLedger] = useState(false)
  // ★★★ v242.146.0 MEDSTOP-LEDGERID-1 — THE ID, UNDER EITHER NAME.
  //   The ledger list returns rows keyed `id`; the profile mirror the API
  //   derives from it keys the same value `ledgerId` (api/index.py:16239). This
  //   app read only the first, so every row that arrived through the mirror was
  //   unaddressable and every write against it was silently skipped.
  //   ★ WHETHER A ROW CAN BE WRITTEN IS A FACT ABOUT THE ROW, NOT ABOUT WHERE
  //     THE LIST CAME FROM — which is why `useLedger` is no longer part of it.
  const ledgerIdOf = (m) => m?.id || m?.ledgerId || m?.ledger_id || null
  const [medError, setMedError] = useState('')
  const searchInputRef = useRef(null)

  // v242.136.0 · MED-PRESCRIBEDBY-ENCODING-1 — rows written by encounter finalize before
  // API v243.148.0 hold `prescribed_by` as a JSON *string scalar*, because that writer
  // json.dumps'd a dict into a JSONB column the API layer already serialises. Reading
  // `.doctorName` off a string gives undefined, which is why every prescribed medication
  // has shown "Rx · Dr. Unknown". The API is fixed for new rows and parses on read; this
  // parses here too so the ledger endpoint's raw rows render for existing patients without
  // a PHI migration. Returns an object or null — never a string.
  const parsePrescribedBy = (raw) => {
    if (!raw) return null
    if (typeof raw === 'object') return raw
    if (typeof raw !== 'string') return null
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : null
    } catch { return null }
  }

  // Load medications — try ledger API first, fall back to profile JSONB
  useEffect(() => {
    const loadMeds = async () => {
      try {
        const { medicationsAPI } = await import('../../services/api')
        const meds = await medicationsAPI.list(true)
        if (meds && meds.length >= 0) {
          // Normalize API field names to camelCase
          const normalized = meds.map(m => ({
            ...m,
            medType: m.med_type || m.medType || 'chronic',
            durationDays: m.duration_days || m.durationDays,
            endDate: m.end_date || m.endDate,
            dateStarted: (m.start_date || m.dateStarted || '')?.slice(0, 7),
            // * `dateStarted` is truncated to YYYY-MM because the edit form is an
            //   <input type="month">. The DISPLAY wants the day, so keep both rather than
            //   let the form's input type decide what the chart is allowed to say.
            startDate: m.start_date || m.startDate || null,
            prescribedBy: parsePrescribedBy(m.prescribed_by || m.prescribedBy),
            discontinuedAt: m.discontinued_at || m.discontinuedAt,
            discontinuedReason: m.discontinued_reason || m.discontinuedReason,
            medicationRef: m.medication_ref || m.medicationRef,
          }))
          setMedications(normalized)
          setHasMedications(normalized.some(m => m.status === 'active'))
          setUseLedger(true)
          setLoading(false)
          return
        }
      } catch (err) {
        console.log('[Medications] Ledger API not available, falling back to profile:', err.message)
      }
      
      // Fallback to profile JSONB
      if (profile?.medications) {
        setHasMedications(profile.medications.hasMedications)
        let list = (profile.medications.list || []).map(m => ({
          ...m, status: m.status || 'active', medType: m.medType || 'chronic', source: m.source || 'self_reported'
        }))
        setMedications(list)
        if (list.length > 0) setHasMedications(true)
      }
      setLoading(false)
    }
    loadMeds()
    if (profile?.preferredPharmacy) setPreferredPharmacy(profile.preferredPharmacy)
  }, [profile])

  const activeMeds = medications.filter(m => m.status === 'active')
  const pastMeds = medications.filter(m => m.status !== 'active')

  // Persist the preferred pharmacy the moment it's selected, entered, or cleared —
  // independent of the medications Yes/No answer and the section Save button (which is
  // gated by canSave). Uses the same profileAPI.update path as the rest of the app.
  const handlePharmacySave = async (pd) => {
    setPreferredPharmacy(pd)
    updateProfile({ preferredPharmacy: pd })
    try {
      const { profileAPI } = await import('../../services/api')
      await profileAPI.update({ preferredPharmacy: pd })
    } catch (err) { console.error('Failed to save pharmacy:', err) }
  }

  // Save — for the "Save & Continue" button (pharmacy + hasMedications flag)
  const handleSave = async () => {
    // Save pharmacy via profile
    if (preferredPharmacy) {
      try {
        const { profileAPI } = await import('../../services/api')
        await profileAPI.update({ preferredPharmacy })
        updateProfile({ preferredPharmacy })
      } catch (err) { console.error('Failed to save pharmacy:', err) }
    }
    
    // If not using ledger, save meds to profile JSONB (backward compat)
    if (!useLedger) {
      const medicationData = { hasMedications, list: medications, completed: true, noMedications: hasMedications === false }
      updateProfile({ medications: medicationData })
      try {
        const { profileAPI } = await import('../../services/api')
        await profileAPI.update({ medications: medicationData, preferredPharmacy })
      } catch (err) { console.error('Failed to sync medications:', err) }
    } else {
      // Mark profile section as completed
      try {
        const { profileAPI } = await import('../../services/api')
        await profileAPI.update({ medications: { hasMedications: activeMeds.length > 0, noMedications: activeMeds.length === 0, completed: true, list: activeMeds.map(m => ({ name: m.name, dosage: m.dosage, frequency: m.frequency, purpose: m.purpose })) } })
      } catch (err) { console.error('Failed to mark medications complete:', err) }
    }

    // ★ P7 P2 (TEST 2.09) — back to the reopen draft, not always the Profile tab.
    const { path, state } = resolveProfileReturn(location.state)
    navigate(path, { state })
  }

  // Add medication
  const handleAddMedication = async () => {
    if (!newMedication.name.trim()) return
    
    let medToSave = { ...newMedication, dosage: formatDose(newMedication.doseAmount, newMedication.doseUnit) }
    delete medToSave.doseAmount
    delete medToSave.doseUnit
    // Calculate end date for courses
    if (medToSave.medType === 'course' && medToSave.durationDays) {
      const start = medToSave.dateStarted ? new Date(medToSave.dateStarted + '-01') : new Date()
      const end = new Date(start); end.setDate(end.getDate() + medToSave.durationDays)
      medToSave.endDate = end.toISOString().split('T')[0]
      if (!medToSave.dateStarted) medToSave.dateStarted = start.toISOString().slice(0, 7)
    }
    
    if (useLedger) {
      try {
        const { medicationsAPI } = await import('../../services/api')
        const editId = ledgerIdOf(editingMed)
        if (editId) {
          // Update existing
          const updated = await medicationsAPI.update(editId, medToSave)
          setMedications(medications.map(m => ledgerIdOf(m) === editId ? { ...m, ...medToSave, ...updated } : m))
        } else {
          // Add new
          const created = await medicationsAPI.add(medToSave)
          setMedications([...medications, { ...medToSave, ...created, status: 'active' }])
        }
      } catch (err) {
        console.error('Ledger save failed:', err)
        // ★ The local fallback stays — an edit should not evaporate as you type —
        //   but it stops passing itself off as saved. "Saved" and "saved on this
        //   device only" are different facts and only one was being shown.
        setMedError(t('profileMedications.saveNotSynced',
          "Saved on this device only — we couldn't reach the server. Try again to sync."))
        // Fallback: add locally
        if (editingMed) {
          setMedications(medications.map(m => m === editingMed ? { ...m, ...medToSave } : m))
        } else {
          setMedications([...medications, { ...medToSave, status: 'active' }])
        }
      }
    } else {
      if (editingMed) {
        setMedications(medications.map(m => m === editingMed ? { ...m, ...medToSave } : m))
      } else {
        setMedications([...medications, { ...medToSave, status: 'active' }])
      }
    }
    
    setNewMedication({ ...DEFAULT_MED }); setMedSearch('')
    setShowAddModal(false); setEditingMed(null); setHasMedications(true)
  }

  const selectFromList = (med) => {
    setNewMedication(prev => ({
      ...prev, name: med.name, purpose: prev.purpose || med.defaultPurpose || '',
      medType: inferMedType(med.name, med.category), medicationRef: med.id, category: med.category,
    }))
    setMedSearch('')
  }

  const handleEditMedication = (med) => {
    setNewMedication({ ...DEFAULT_MED, ...med, ...parseDose(med.dosage) })
    setEditingMed(med); setMedSearch(''); setShowAddModal(true)
  }

  // ★★★ "IF I SELECT NO EVERYTHING DISAPPEARS" (TF b45 #4 and #5). Answering the
  //     present-tense question "do you take medications" with None used to hide the
  //     active list behind `hasMedications &&`. The rows were never lost — they stayed
  //     in state and in the saved payload — but the patient watched their medication
  //     list vanish with no explanation and no way back except answering Yes again.
  //     A list that disappears is indistinguishable from a list that was DELETED, and
  //     on a medication screen that is the worst ambiguity the screen can produce.
  //
  // ★★★ AND THE FIX IS NOT AN "ARE YOU SURE" DIALOG. Saying None while rows are
  //     listed is a real clinical statement — the patient has STOPPED taking them — so
  //     the rows move to Past through the SAME discontinue path the per-row stop
  //     button already uses. One writer, not two. Nothing is deleted, the history is
  //     preserved, and Past is expanded on the way out so the patient SEES where the
  //     rows went. Being shown the destination is what makes the move legible.
  const handleChooseNone = () => {
    if (activeMeds.length > 0) { setShowNoneConfirm(true); return }
    setHasMedications(false)
  }

  const confirmNone = async () => {
    setMovingToPast(true)
    const reason = t('profileMedications.noneReason')
    const stamp = new Date().toISOString()
    const toMove = activeMeds
    // ★ Same resolver, and the failures are COUNTED rather than swallowed: this
    //   loop used to abandon the whole batch on the first error and then mark
    //   every row stopped locally anyway.
    let failed = 0
    try {
      const { medicationsAPI } = await import('../../services/api')
      for (const m of toMove) {
        const id = ledgerIdOf(m)
        if (!id) continue
        try { await medicationsAPI.discontinue(id, reason) } catch (e) { failed++ }
      }
    } catch (err) { console.error('Move-to-past failed:', err); failed = toMove.length }
    if (failed) {
      setMedError(t('profileMedications.stopSomeFailed',
        "Some medications couldn't be stopped. Check your connection and try again."))
      setMovingToPast(false)
      setShowNoneConfirm(false)
      return
    }
    setMedError('')
    setMedications((meds) => meds.map((m) => (
      m.status === 'active'
        ? { ...m, status: 'discontinued', discontinuedAt: stamp, discontinuedReason: reason }
        : m
    )))
    setHasMedications(false)
    setShowPastMeds(true)
    setShowNoneConfirm(false)
    setMovingToPast(false)
  }

  const handleDiscontinue = async () => {
    if (!showDiscontinueModal) return
    const med = showDiscontinueModal
    
    // ★★★ MEDSTOP-REASON-1 — no fallback. The picker below already disables the button
    //   until a reason is chosen, so `|| 'No longer needed'` was unreachable — but it was
    //   also the same invented clinical statement the API used to substitute, waiting for
    //   a second caller. A stop is a decision; if nobody said why, nothing claims they did.
    if (!discontinueReason) return
    const medId = ledgerIdOf(med)
    if (medId) {
      try {
        const { medicationsAPI } = await import('../../services/api')
        await medicationsAPI.discontinue(medId, discontinueReason)
      } catch (err) {
        // ★★ The API refuses a blank reason with a 400. If the write failed, the row did
        //   NOT stop — saying otherwise locally is how a cancelled action comes back on
        //   reload looking like a completed one.
        // ★★★ AND IT NOW SAYS SO. Returning in silence is what made this read as a
        //   dead button: a stop that failed and a stop that never fired looked
        //   identical until the next reload put the medication back.
        console.error('Discontinue failed:', err)
        setMedError(t('profileMedications.stopFailed',
          "We couldn't stop this medication. Check your connection and try again."))
        return
      }
    }
    setMedError('')

    // ★ v242.148.0 — the shorthand said `discontinuedReason`; the state is
    //   `discontinueReason`. Naming the source explicitly is what stops a
    //   shorthand from silently referring to a name that does not exist.
    setMedications(medications.map(m => m === med ? {
      ...m, status: 'discontinued', discontinuedAt: new Date().toISOString(),
      discontinuedReason: discontinueReason
    } : m))
    setShowDiscontinueModal(null); setDiscontinueReason('')
  }

  // ★★★ v242.174.0 · MED-SCREEN-SURFACE-1 — "STILL TAKING IT". His ruling, 2026-09-08,
  //   chosen between three shapes: REOPEN THE SAME COURSE, rather than starting a new row
  //   and leaving the finished one in Past. The same row comes back active, so its
  //   provenance survives — re-adding it would have made a doctor's prescription read
  //   "You added this", which is the fact the Past section exists to keep.
  //   ★★ THE SERVER STASHES WHAT WAS PRESCRIBED before it clears the end date, so the
  //   record can still say a 10-day course was ordered and the patient took longer.
  //   ★★★ A 403 IS NOT A FAILURE TO SWALLOW. It is the server saying a DOCTOR stopped this
  //   one, and that sentence is the answer — not "try again".
  const [resumingId, setResumingId] = useState(null)
  const handleResume = async (med) => {
    const medId = ledgerIdOf(med)
    if (!medId || resumingId) return
    setMedError('')
    setResumingId(medId)
    try {
      const { medicationsAPI } = await import('../../services/api')
      const res = await medicationsAPI.resume(medId)
      const row = res?.medication || {}
      setMedications(medications.map(m => m === med ? {
        ...m,
        status: 'active',
        discontinuedAt: null,
        discontinuedReason: null,
        endDate: null,
        end_date: null,
        originalEndDate: row.original_end_date || m.endDate || m.end_date || null,
        resumedAt: row.resumed_at || new Date().toISOString(),
      } : m))
    } catch (err) {
      console.error('Resume failed:', err)
      // ★ The server's own sentence when it has one — a 403 here names the doctor's stop,
      //   and replacing it with a generic retry message would hide the only useful fact.
      setMedError(err?.error || t('profileMedications.resumeFailed',
        "We couldn't restart this medication. Check your connection and try again."))
    } finally {
      setResumingId(null)
    }
  }

  // ★★★ A medication the DOCTOR put there is read-only to the patient (his ruling) and the
  //   API refuses to delete it — `_med_delete` returns 400 for `prescribed`. The row used to
  //   be filtered out of local state ANYWAY, so tapping Remove made it vanish and then
  //   return on the next load. The button is no longer offered for those rows, and the
  //   removal is no longer unconditional. Stopping one is what the patient can do.
  const isDoctorSourced = (med) => med?.source === 'prescribed' || med?.source === 'md_entered'

  const handleRemoveMedication = async (med) => {
    if (isDoctorSourced(med)) return
    const medId = ledgerIdOf(med)
    if (medId) {
      try {
        const { medicationsAPI } = await import('../../services/api')
        await medicationsAPI.remove(medId)
      } catch (err) {
        console.error('Delete failed:', err)
        setMedError(t('profileMedications.removeFailed',
          "We couldn't remove this medication. Check your connection and try again."))
        return
      }
    }
    setMedError('')
    setMedications(medications.filter(m => m !== med))
  }

  const canSave = hasMedications !== null
  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors"
  const normalizedSearch = medSearch.toLowerCase().trim()
  const commonMeds = COMMON_MEDICATIONS.filter(m => m.common)
  const searchResults = normalizedSearch ? COMMON_MEDICATIONS.filter(m => m.name.toLowerCase().includes(normalizedSearch) || (m.category && m.category.toLowerCase().includes(normalizedSearch))) : []

  const StatusBadge = ({ med }) => {
  const { t } = useI18n()
    if (med.status === 'discontinued') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">{t('profileMedications.stopped')}</span>
    if (med.status === 'completed') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t('profileMedications.completed')}</span>
    const days = daysRemaining(med)
    if (med.medType === 'course' && days !== null) {
      if (days <= 0) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t('profileMedications.completed')}</span>
      if (days <= 3) return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600">{days}d left</span>
      return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#E9F8FF', color: '#46A8F9' }}>{days}d left</span>
    }
    if (med.medType === 'chronic') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#D5EFD3', color: '#16A34A' }}>{t('profileMedications.ongoing')}</span>
    // ★★★ v242.177.0 — THREE OF THESE FIVE BADGES WERE HARDCODED ENGLISH AND TWO WERE
    //   NOT, in one function, on one row of one card. `ongoing` and `active` below go
    //   through t(); Course, Supplement and As Needed did not — AND THE KEYS THEY NEEDED
    //   ALREADY EXISTED, translated, in en and es. Nothing had to be written; the calls
    //   were simply never made. The parity ratchet cannot see this class at all, which is
    //   why MED-SCREEN-SURFACE-1's headers were found by reading the file and not by a
    //   build. Swept the whole screen for JSX text nodes: these three, the two section
    //   headers, and the discontinue question were all of them.
    if (med.medType === 'course') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#E9F8FF', color: '#46A8F9' }}>{t('profileMedications.course')}</span>
    if (med.medType === 'supplement') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#F3E8FF', color: '#7C3AED' }}>{t('profileMedications.supplement')}</span>
    if (med.medType === 'otc') return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t('profileMedications.asNeeded')}</span>
    return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#D5EFD3', color: '#16A34A' }}>{t('profileMedications.active')}</span>
  }

  const getMedDisplay = (med) => [med.dosage, med.frequency, med.purpose].filter(Boolean).join(' \u00B7 ')

  // ★★ WHO MAY REOPEN THIS. Mirrors `_med_resume` on the server, which is the authority —
  //   this only decides whether to OFFER the tap, so a patient is never shown an
  //   affordance that answers 403. NULL `stopped_by_role` is UNKNOWN and is allowed: every
  //   row written before v243.240.0 has NULL, and most of them are expired courses with no
  //   actor at all, which is the whole "Still taking it" case.
  const canResume = (med) => {
    if (!ledgerIdOf(med)) return false
    if (med?.status !== 'completed' && med?.status !== 'discontinued') return false
    return (med.stoppedByRole || med.stopped_by_role) !== 'md'
  }
  // ★★★ MED-PROVENANCE-LABEL-1 — the old `getMedSubline` ended in
  //     `else parts.push(t('profileMedications.selfReported'))`, which labelled EVERY row that
  //     was not a prescription-with-a-resolvable-prescriber as the patient's own report —
  //     including every `md_entered` row and every prescribed row whose prescriber was blank.
  //     The decision now lives in `utils/medicationProvenance.js` where it can be gated.
  const MedProvenance = ({ med }) => {
    const p = medicationProvenance(med)
    if (p.kind === 'doctor') {
      return (
        <p className="text-[11px] font-semibold mt-1 flex items-center gap-1.5" style={{ color: '#15354F' }}>
          <span aria-hidden="true" style={{ color: '#46A8F9', fontSize: '9px', lineHeight: 1 }}>●</span>
          {p.doctorName
            ? t('profileMedications.addedByDr', { ...clinicianForms(clinicianFromText(p.doctorName)), name: p.doctorName, defaultValue: `Added by ${clinicianName(clinicianFromText(p.doctorName))}` })
            : t('profileMedications.addedByYourDoctor', 'Added by your doctor')}
        </p>
      )
    }
    return <p className="text-[11px] text-gray-400 mt-1">{t('profileMedications.youAddedThis', 'You added this')}</p>
  }

  const renderMedCard = (med, isPast = false) => (
    <div key={med.id || med.name + med.dosage} className={`rounded-[14px] border-[1.5px] overflow-hidden transition-all ${isPast ? 'border-gray-200 opacity-60' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-3 px-3.5 py-3">
        <div className="flex-1 min-w-0" onClick={() => !isPast && handleEditMedication(med)} style={{ cursor: isPast ? 'default' : 'pointer' }}>
          <div className="flex items-start gap-2 mb-0.5">
            <p className={`text-[13px] font-bold leading-snug break-words ${isPast ? 'line-through text-gray-400' : ''}`} style={!isPast ? { color: '#15354F' } : {}}>
              {med.name}{med.dosage ? ` \u2014 ${med.dosage}` : ''}
            </p>
            <span className="flex-shrink-0 mt-0.5"><StatusBadge med={med} /></span>
          </div>
          {getMedDisplay(med) && <p className="text-[11px] text-gray-500">{getMedDisplay(med)}</p>}
          {medicationTimespan(med).text && (
            <p className="text-[11px] text-gray-400 mt-0.5">{medicationTimespan(med).text}</p>
          )}
          <MedProvenance med={med} />
          {med.discontinuedReason && med.status !== 'active' && (
            <p className="text-[11px] text-gray-400 mt-0.5">{med.discontinuedReason}</p>
          )}
          {/* ★★★ v242.174.0 — THE WAY BACK OUT OF PAST. Until now a row that reached this
              section was unreachable: the card is cursor:default, the click is suppressed,
              and the only route back was ADDING IT AGAIN — which loses its history and its
              provenance, so a doctor's prescription came back reading "You added this".
              ★★ THE WORDING FOLLOWS THE FACT. A course that reached its end date was not a
              decision anybody made, so the question is whether the patient is still taking
              it. A medication somebody STOPPED was a decision, so the question is whether
              they have restarted it. Same call, two different things to say.
              ★ Offered only where the server will accept it: a stop a DOCTOR made is not
              the patient's to undo from an app. `stopped_by_role` is NULL on every row
              written before v243.240.0 and that is read as unknown, not as a doctor. */}
          {isPast && canResume(med) && (
            <button
              onClick={() => handleResume(med)}
              disabled={resumingId === ledgerIdOf(med)}
              className="mt-1.5 text-[11px] font-bold disabled:opacity-50"
              style={{ color: '#46A8F9' }}
            >
              {resumingId === ledgerIdOf(med)
                ? t('common.saving', 'Saving…')
                : med.status === 'completed'
                  ? t('profileMedications.stillTakingIt', 'Still taking it')
                  : t('profileMedications.restartedThis', "I've restarted this")}
            </button>
          )}
        </div>
        {!isPast && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => { setShowDiscontinueModal(med); setDiscontinueReason('') }} className="p-1.5 rounded-lg active:scale-95" title={t('profileMedications.discontinue')}>
              <X size={14} className="text-gray-400" />
            </button>
            <button onClick={() => handleEditMedication(med)} className="p-1.5 rounded-lg active:scale-95">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#46A8F9" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      <ProfileSubpageLayout sectionId="medications" icon={Pill} onSave={handleSave} saving={false} canSave={canSave}>
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileMedications.preferredPharmacy')}</p>
        <p className="text-[12px] text-gray-400 mb-3">{t('profileMedications.preselectedWhenSharingCasesWith')}</p>
        <div className="mb-5 pb-5 border-b border-gray-100">
          <PharmacyLookup savedPharmacy={preferredPharmacy} allowSkip={false} onSave={handlePharmacySave} />
        </div>

        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileMedications.myMedications')}</p>

        <div className="flex gap-3 mb-4">
          <button onClick={() => setHasMedications(true)}
            className={`flex-1 py-2.5 rounded-[14px] text-[13px] font-semibold transition-all ${hasMedications === true ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 text-gray-400 border-[1.5px] border-gray-200'}`}>
            {t('common.yes')}
          </button>
          <button onClick={handleChooseNone}
            className={`flex-1 py-2.5 rounded-[14px] text-[13px] font-semibold transition-all ${hasMedications === false ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 text-gray-400 border-[1.5px] border-gray-200'}`}>
            {t('profileMedications.none')}
          </button>
        </div>

        {hasMedications && (
          <div className="space-y-2 mb-4">
            {activeMeds.length > 0 && (
              <div className="space-y-2">
                {/* ★★★ v242.177.0 — HARDCODED ENGLISH INSIDE A SPANISH SCREEN.
                    Same class as D419: a string that never becomes a key is one the
                    parity ratchet is STRUCTURALLY UNABLE to count as missing, so this
                    header sat in English under a fully-translated screen and no gate
                    anywhere could have said so. es is a pilot locale. */}
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 px-1">{t('profileMedications.activeCount', { count: activeMeds.length, defaultValue: 'Active ({{count}})' })}</p>
                {activeMeds.map(med => renderMedCard(med))}
              </div>
            )}

            {/* ★★★ v242.177.0 — THE EMPTY STATE MED-SCREEN-SURFACE-1 ASKED FOR.
                A patient who answered YES and has nothing on the ledger saw a dashed
                button and NO WORDS AT ALL — not "none", not an invitation, nothing. The
                row asks for a state that invites rather than reporting; there was no
                state at all to report with.
                ★★ TWO SENTENCES, BECAUSE THEY ARE TWO DIFFERENT FACTS. Someone whose
                every medication has ENDED is not starting from nothing, and telling them
                so would hide the history sitting one block below. `pastMeds` is derived
                independently of the toggle (v242.87.0), so it is readable here.
                ★★★ AND THE COPY IS THE MOCKUP'S, NOT MINE. The first draft of this block
                was written from the row's own words because claude/orella_medications_mockup_v1
                .html could not be found — it had been ARCHIVED to orella-api/docs/archive/
                mockups/ hours earlier by a pass that had not committed yet, so it was on disk,
                untracked, and invisible to a project search. A DEAD POINTER AND A MOVED ONE
                LOOK IDENTICAL FROM WHERE I WAS STANDING, and the difference is a design
                somebody already agreed. Title, body and the first-add label are the mockup's.
                ★ The all-past branch is NOT in the mockup and is a stated addition, not a
                slip: the mockup draws one empty state, and a patient whose every course has
                ENDED is a different person to talk to. */}
            {activeMeds.length === 0 && (
              <div className="px-1 pb-1">
                <p className="text-[13px] font-bold mb-1" style={{ color: '#15354F' }}>
                  {t('profileMedications.emptyTitle', 'Let\u2019s add your medications')}
                </p>
                <p className="text-[12px] text-gray-400 leading-snug">
                  {pastMeds.length > 0
                    ? t('profileMedications.emptyAllPast', 'Nothing active right now. What you used to take is under Past, below.')
                    : t('profileMedications.emptyInvite', 'It takes about a minute, and it means your doctor sees the full picture before your visit.')}
                </p>
              </div>
            )}

            <button onClick={() => { setNewMedication({ ...DEFAULT_MED }); setEditingMed(null); setMedSearch(''); setShowAddModal(true) }}
              className="w-full py-2.5 border-[1.5px] border-dashed rounded-[14px] text-[13px] font-semibold flex items-center justify-center gap-2" style={{ borderColor: 'rgba(70,168,249,0.3)', color: '#46A8F9' }}>
              <Plus size={16} /> {activeMeds.length === 0 && pastMeds.length === 0
                ? t('profileMedications.addFirstMedication', 'Add my first medication')
                : t('profileMedications.addMedication')}
            </button>
          </div>
        )}

        {/* ★★ v242.87.0 (TF b35 #18) — "If I click on medication 'No' the past
            section also disappears. It should be still there imo."
            He is right, and it is a scoping bug, not a policy one. The Past
            block used to live INSIDE the `hasMedications &&` gate above, so
            answering No hid a patient's entire medication HISTORY. The Yes/No
            question is "do you take medications" — present tense — and says
            nothing about what they used to take. The data was never lost:
            `pastMeds` is derived from `medications` independently of the toggle,
            so the rows were in state the whole time and simply were not
            rendered. Moving the block out is the whole fix; nothing about
            persistence changes, because handleSave already writes
            `list: activeMeds` and `hasMedications` separately. */}
        {pastMeds.length > 0 && (
          <div className="mt-3 mb-4">
            <button onClick={() => setShowPastMeds(!showPastMeds)} className="flex items-center gap-2 w-full py-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300">{t('profileMedications.pastCount', { count: pastMeds.length, defaultValue: 'Past ({{count}})' })}</p>
              {showPastMeds ? <ChevronUp size={14} className="text-gray-300" /> : <ChevronDown size={14} className="text-gray-300" />}
            </button>
            {showPastMeds && <div className="space-y-2">{pastMeds.map(med => renderMedCard(med, true))}</div>}
          </div>
        )}
      </ProfileSubpageLayout>

      {/* ═══ Add/Edit Modal ═══ */}
      {/* v242.30.0 (FB4): portal to document.body — this full-screen fixed modal was
          rendering inside PageWrapper's framer-motion transform, so a transformed
          ancestor became the containing block for position:fixed. When the list was
          scrolled before opening, the modal anchored to the page box (not the viewport)
          and its scroll region + footer landed off-screen — the "wrapper is not
          scrollable" report. Header also now clears the top safe-area inset. */}
      {showAddModal && createPortal((
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'white', zIndex: 99999, display: 'flex', flexDirection: 'column', height: '100dvh' }}>
          <div style={{ padding: '12px 16px', paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <h3 className="text-[16px] font-bold" style={{ color: '#15354F' }}>{editingMed ? t('profileMedications.editMedication') : t('profileMedications.addMedication')}</h3>
            <button onClick={() => { setShowAddModal(false); setMedSearch('') }} style={{ padding: '8px', background: 'none', border: 'none' }}><X className="text-gray-400" size={22} /></button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
            <div className="space-y-4">
              {/* Name search */}
              <div>
                <label className="block text-[12px] text-gray-500 mb-1">{t('profileMedications.medicationName')} <span className="text-red-500">*</span></label>
                {newMedication.name ? (
                  <div className="flex items-center gap-2 px-3.5 py-2.5 border-[1.5px] border-[#46A8F9] bg-[#E9F8FF] rounded-[14px]">
                    <Pill size={16} style={{ color: '#46A8F9' }} />
                    <span className="text-[13px] font-semibold flex-1" style={{ color: '#15354F' }}>{newMedication.name}</span>
                    <button onClick={() => { setNewMedication({ ...newMedication, name: '', purpose: '', medicationRef: null, category: null }); setMedSearch(''); setTimeout(() => searchInputRef.current?.focus(), 50) }}>
                      <X size={16} className="text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input ref={searchInputRef} type="text" value={medSearch} onChange={(e) => setMedSearch(e.target.value)}
                        placeholder={t('profileMedications.searchPlaceholder')} className="w-full pl-10 pr-10 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9]" style={{ color: '#15354F' }} autoFocus />
                      {medSearch && <button onClick={() => setMedSearch('')} className="absolute right-3.5 top-1/2 -translate-y-1/2"><X size={16} className="text-gray-400" /></button>}
                    </div>
                    {normalizedSearch && (
                      <div className="mt-2 space-y-1 max-h-[200px] overflow-y-auto">
                        {searchResults.length > 0 ? searchResults.slice(0, 8).map(med => (
                          <button key={med.id} onClick={() => selectFromList(med)}
                            className="w-full text-left px-3 py-2 rounded-[10px] border-[1.5px] border-gray-200 active:scale-[0.98] transition-all hover:border-[#46A8F9] hover:bg-[#E9F8FF]">
                            <p className="text-[13px] font-medium" style={{ color: '#15354F' }}>{med.name}</p>
                            <p className="text-[10px] text-gray-400">{med.category}</p>
                          </button>
                        )) : <p className="text-[12px] text-gray-400 text-center py-2">{t('profileMedications.noMatchesFound')}</p>}
                        <button onClick={() => { setNewMedication({ ...newMedication, name: medSearch.trim(), medType: 'chronic' }); setMedSearch('') }}
                          className="w-full text-left px-3 py-2 rounded-[10px] border-[1.5px] border-dashed active:scale-[0.98]" style={{ borderColor: 'rgba(70,168,249,0.3)' }}>
                          {/* ★★★ v242.180.0 — THE SEVENTH STRING ON THIS SCREEN, AND THE GATE PINNED AT ZERO SAID
                              THERE WERE NONE. jsxText's punctuation class listed only ASCII, so a run
                              containing an HTML entity or a typographic character ended there and failed
                              the is-this-a-sentence test. `Use "<what they typed>" as entered` was
                              invisible to the arm written for exactly this screen. Widened 2026-09-09;
                              eighteen more strings appeared app-wide in the same pass. */}
                          <p className="text-[13px] font-semibold" style={{ color: '#46A8F9' }}>{t('profileMedications.useAsEntered', { name: medSearch.trim(), defaultValue: 'Use \u201c{{name}}\u201d as entered' })}</p>
                          <p className="text-[10px] text-gray-400">{t('profileMedications.notInOurListThatrsquos')}</p>
                        </button>
                      </div>
                    )}
                    {!normalizedSearch && (
                      <div className="mt-2 space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-300 px-1 mb-1">{t('profileMedications.common')}</p>
                        {commonMeds.map(med => (
                          <button key={med.id} onClick={() => selectFromList(med)}
                            className="w-full text-left px-3 py-2 rounded-[10px] border-[1.5px] border-gray-200 active:scale-[0.98] transition-all hover:border-[#46A8F9] hover:bg-[#E9F8FF]">
                            <p className="text-[13px] font-medium" style={{ color: '#15354F' }}>{med.name}</p>
                            <p className="text-[10px] text-gray-400">{med.category}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {newMedication.name && (
                <>
                  {/* Med Type */}
                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1.5">{t('profileMedications.type')}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {MED_TYPES.map(type => (
                        <button key={type.id} onClick={() => setNewMedication({ ...newMedication, medType: type.id, durationDays: type.id !== 'course' ? null : newMedication.durationDays, endDate: type.id !== 'course' ? null : newMedication.endDate })}
                          className={`px-3 py-2.5 rounded-[14px] border-[1.5px] text-left transition-all active:scale-[0.97] ${newMedication.medType === type.id ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200'}`}>
                          <p className={`text-[12px] font-semibold ${newMedication.medType === type.id ? '' : 'text-gray-600'}`} style={newMedication.medType === type.id ? { color: '#15354F' } : {}}>{type.label}</p>
                          <p className="text-[10px] text-gray-400">{t('profileMedications.' + type.descKey)}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {newMedication.medType === 'course' && (
                    <div>
                      <label className="block text-[12px] text-gray-500 mb-1">{t('profileMedications.courseDuration')}</label>
                      <div className="flex flex-wrap gap-1.5">
                        {COURSE_DURATIONS.map(d => (
                          <button key={d.days} onClick={() => setNewMedication({ ...newMedication, durationDays: d.days })}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all ${newMedication.durationDays === d.days ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 text-gray-500 border-[1.5px] border-gray-200'}`}>
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1">{t('profileMedications.dosage')}</label>
                    <div className="flex gap-2">
                      <input type="text" inputMode="decimal" value={newMedication.doseAmount}
                        onChange={(e) => setNewMedication(m => ({ ...m, doseAmount: e.target.value, dosage: formatDose(e.target.value, m.doseUnit) }))}
                        placeholder={t('profileMedications.dosageAmountPh', { defaultValue: '10' })}
                        style={{ width: 96, height: 42, border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '0 12px', fontSize: 14, color: '#15354F', fontWeight: 600, outline: 'none' }} />
                      <div className="flex-1">
                        <WheelPicker value={newMedication.doseUnit} options={MED_DOSE_UNITS}
                          onChange={(u) => setNewMedication(m => ({ ...m, doseUnit: u, dosage: formatDose(m.doseAmount, u) }))}
                          title={t('profileMedications.doseUnit', { defaultValue: 'Unit' })}
                          doneLabel={t('common.done', { defaultValue: 'Done' })} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1">{t('profileMedications.frequency')}</label>
                    <WheelPicker value={newMedication.frequency} options={FREQ_OPTS}
                      onChange={(v) => setNewMedication(m => ({ ...m, frequency: v }))}
                      title={t('profileMedications.frequency')}
                      doneLabel={t('common.done', { defaultValue: 'Done' })} />
                  </div>
                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1">{t('profileMedications.purposeOptional')}</label>
                    <input type="text" value={newMedication.purpose} onChange={(e) => setNewMedication({...newMedication, purpose: e.target.value})}
                      placeholder={t('profileMedications.purposePlaceholder')} className={inputCls} style={{ color: '#15354F' }} />
                  </div>
                  <div>
                    <label className="block text-[12px] text-gray-500 mb-1">{t('profileMedications.dateStartedOptional')}</label>
                    <input type="month" value={newMedication.dateStarted} onChange={(e) => setNewMedication({...newMedication, dateStarted: e.target.value})}
                      className={inputCls} style={{ color: '#15354F' }} />
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={{ padding: '16px', paddingBottom: '100px', borderTop: '1px solid #e5e7eb', backgroundColor: 'white', flexShrink: 0 }}>
            <div className="flex gap-3">
              {editingMed && !isDoctorSourced(editingMed) && (
                <button onClick={() => { handleRemoveMedication(editingMed); setShowAddModal(false); setEditingMed(null); setMedSearch('') }}
                  className="flex-1 py-3 border-[1.5px] border-gray-300 text-gray-600 rounded-2xl font-semibold text-[14px]">{t('profileMedications.remove')}</button>
              )}
              <button onClick={handleAddMedication} disabled={!newMedication.name.trim()}
                className={`flex-1 py-3 rounded-2xl font-bold text-[14px] ${newMedication.name.trim() ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                style={newMedication.name.trim() ? { background: '#15354F' } : {}}>
                {editingMed ? t('profileMedications.update') : t('profileMedications.add')}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* ═══ None-with-rows-listed confirm ═══ */}
      {/* Amber, not red: this is the palette's one ATTENTION signal and it is a FILL
          (D220). Nothing here is destructive — there is no red in the Orella palette. */}
      {showNoneConfirm && createPortal((
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ background: '#FFFBEB', border: '1.5px solid #FDE68A' }}>
              <AlertTriangle size={22} style={{ color: '#F59E0B' }} />
            </div>
            <h3 className="text-[16px] font-bold mb-2" style={{ color: '#15354F' }}>{t('profileMedications.noneConfirmTitle')}</h3>
            <p className="text-[13px] text-gray-500">{t('profileMedications.noneConfirmBody')}</p>
            <ul className="mt-3 mb-4 space-y-1">
              {activeMeds.map((m) => (
                <li key={m.id || m.name + m.dosage} className="text-[12.5px] font-semibold" style={{ color: '#15354F' }}>
                  {'\u00b7'} {m.name}
                </li>
              ))}
            </ul>
            <button onClick={confirmNone} disabled={movingToPast}
              className="w-full py-3 rounded-2xl font-bold text-[14px] text-white mb-2 disabled:opacity-50"
              style={{ background: '#15354F' }}>
              {movingToPast ? t('profileMedications.movingToPast') : t('profileMedications.noneConfirmCta')}
            </button>
            <button onClick={() => setShowNoneConfirm(false)}
              className="w-full py-2.5 rounded-2xl font-semibold text-[13px] text-gray-500">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      ), document.body)}

      {/* ═══ Discontinue Modal ═══ */}
      {showDiscontinueModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-bold" style={{ color: '#15354F' }}>{t('profileMedications.discontinueMedication')}</h3>
              <button onClick={() => setShowDiscontinueModal(null)}><X className="text-gray-400" size={22} /></button>
            </div>
            {/* ★★★ v242.177.0 — A SENTENCE WITH AN ELEMENT INSIDE IT, WHICH IS WHY IT WAS
                STILL ENGLISH. The medication name is bold, so the obvious t() call would
                have had to give the emphasis up, and giving it up is a visible regression
                — so the string stayed hardcoded instead.
                ★★ THE NAME IS DELIBERATELY NOT PASSED AS A PARAM. t() interpolates
                {{name}} when it is given one; withholding it leaves the placeholder in
                the returned string, and splitting on it puts the styled element exactly
                where the TRANSLATOR put the placeholder rather than where English puts
                it. Spanish and English do not order this sentence the same way. */}
            <p className="text-[13px] text-gray-500 mb-4">
              {t('profileMedications.whyStopping', { defaultValue: 'Why are you stopping {{name}}?' })
                .split('{{name}}')
                .flatMap((part, i) => i === 0 ? [part] : [
                  <span key={`n${i}`} className="font-semibold" style={{ color: '#15354F' }}>{showDiscontinueModal.name}</span>,
                  part,
                ])}
            </p>
            <div className="space-y-1.5 mb-4">
              {DISCONTINUE_REASONS.map(reason => (
                <button key={reason} onClick={() => setDiscontinueReason(reason)}
                  className={`w-full text-left px-3.5 py-2.5 rounded-[14px] border-[1.5px] text-[13px] transition-all ${discontinueReason === reason ? 'border-[#46A8F9] bg-[#E9F8FF] font-semibold' : 'border-gray-200 text-gray-600'}`}
                  style={discontinueReason === reason ? { color: '#15354F' } : {}}>{reason}</button>
              ))}
            </div>
            {medError && (
              <p className="text-[12px] mb-2" style={{ color: '#DC2626' }}>{medError}</p>
            )}
            <button onClick={handleDiscontinue} disabled={!discontinueReason}
              className={`w-full py-3 rounded-2xl font-bold text-[14px] ${discontinueReason ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
              style={discontinueReason ? { background: '#15354F' } : {}}>{t('profileMedications.discontinue')}</button>
          </div>
        </div>
        </OverlayPortal>
      )}
    </>
  )
}
